import { CarDamagePictogram } from '@velocity/icons/pictogram'
import { useMemo } from 'react'

import { useQuery } from '@ngb-frontend/shared/context'
import { UserType } from '@ngb-frontend/shared/types'

import { DriverNotificationView } from '../DriverNotificationView/DriverNotificationView'
import { FlexBox } from '../FlexBox/FlexBox'
import { NotificationView } from '../NotificationView/NotificationView'
import { StepLayout as Layout } from '../StepLayout/StepLayout'

import type { FallbackProps } from 'react-error-boundary'

// NB: In dev environment, the NextJS error overlay will still be shown
// https://github.com/vercel/next.js/discussions/13387

export const boundaryErrors = {
  unknownPath: 'Unknown step path',
  generic: 'Unfortunately, something went wrong',
  retry: 'Retry',
  back: 'Back to My Ayvens',
  navLabel: 'Maintenance and repair',
}

/**
 * This component wraps also all providers, hence the message it displays cannot
 * be localized and should instead be in English.
 */
export const ErrorBoundaryFallback: React.FC<FallbackProps> = ({
  error,
  resetErrorBoundary,
}) => {
  // The driver flow's own look; every other persona keeps the card
  const isDriver = useQuery()?.userType === UserType.Driver
  const View = isDriver ? DriverNotificationView : NotificationView

  const ErrorComponent = useMemo(() => {
    if (error.message === boundaryErrors.unknownPath) {
      return (
        <View Icon={CarDamagePictogram} headline={boundaryErrors.unknownPath} />
      )
    }

    return (
      <View
        Icon={CarDamagePictogram}
        headline={boundaryErrors.generic}
        // Strings are intentionally hardcoded English: this boundary wraps the
        // providers, so localized content is unavailable here.
        onSubmit={resetErrorBoundary}
        submitButtonText={boundaryErrors.retry}
        onCancel={() => window.history.back()}
        cancelButtonText={boundaryErrors.back}
        cancelButtonVariant="secondary-outlined"
      />
    )
  }, [View, error.message, resetErrorBoundary])

  return (
    <Layout
      navLabel={isDriver ? boundaryErrors.navLabel : ''}
      showLogo={!isDriver}
    >
      <FlexBox>{ErrorComponent}</FlexBox>
    </Layout>
  )
}
